import { Component, ChangeDetectionStrategy } from "@angular/core";

import { ProfileBaseComponent } from "@mypxplat/xplat/features";
import { UserService, HelperService, ExchangeService, environment, EventBusService, WorkspaceService, NotificationService } from "@mypxplat/xplat/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { AppService, AvoService, FirebaseService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { UploadWareComponent, AddWorkspaceComponent, ReviewConnectionComponent } from "../modals";
import { AddEditSkillComponent } from "../modals/add-edit-skill/add-edit-skill.component";
import { filter, take, takeUntil } from "rxjs/operators";
import { Location } from "@angular/common";
import { SelectListItemComponent } from "../modals/select-list-item/select-list-item.component";
import { AddConnectionComponent } from "../modals/add-connection/add-connection.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-profile",
  templateUrl: "profile.component.html",
  standalone: false,
})
export class ProfileComponent extends ProfileBaseComponent {
  public env = environment;
  public displayFsDetails: boolean = false;
  public routerEvents: any;
  public mySkills: Array<any>;
  public posts: Array<any>;
  public hasBeenInit: boolean = false;
  public connections: Array<any>;
  public displayedConnections: Array<any>;
  public connectionFilter: any;
  public invites: Array<any>;
  public connectionStatus: string;
  public connection: any;
  public workspaces: Array<any>;
  public showcases: Array<any>;
  public hasSetupCommunity: boolean = false;
  public isFeatured: boolean = false;
  public coverPhoto: string;
  public loadingPosts: boolean = false;
  public loadingPins: boolean = false;
  public noPosts: boolean;
  public noPins: boolean;
  public exchangeCategories: any;
  public wareCategories: any = [];
  public displayedWareCategory: any;
  public filteredWares: any;
  constructor(
    userService: UserService,
    public helperService: HelperService,
    public exchangeService: ExchangeService,
    public activatedRoute: ActivatedRoute,
    public appService: AppService,
    public router: Router,
    public communityService: WebCommunityService,
    public location: Location,
    private _eventbusService: EventBusService,
    public workspaceService: WorkspaceService,
    public avoService: AvoService,
    public fbService: FirebaseService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {
    super(userService, exchangeService, activatedRoute);
    this.router.events
      .pipe(
        takeUntil(this.destroy$)
        // filter(item => item.url.indexOf('profile') > -1)
      )
      .subscribe((result) => {
        if (result instanceof NavigationEnd && result.url.indexOf("profile") > -1) {
          this.profile = undefined;
          this.mySkills = undefined;
          this.workspaces = undefined;
          this.posts = undefined;
          this.connections = undefined;
          this.invites = undefined;
          this.coverPhoto = undefined;
          this.loading = true;
          if (this.hasBeenInit) this.ngOnInit();
        }
      });
  }

  private loadProfileWithPrivacyCheck(appProfile: any, identifier: string) {
    this.communityService
      .getProfileDetails(identifier)
      .pipe(take(1))
      .subscribe(
        (creatorResult) => {
          const creatorInternalId = creatorResult?.userId ?? creatorResult?.id;
          const isMe = creatorInternalId != null && String(creatorInternalId) === String(this.user.id);
          this.account_id = creatorInternalId;

          if (creatorResult && creatorResult.isPublic === false && !isMe) {
            this.profile = creatorResult;
            this.mySkills = [];
            this.workspaces = [];
            this.posts = [];
            this.connections = [];
            this.invites = [];
            this.showcases = [];
            this.wares = undefined;
            this.isFeatured = false;
            this.loading = false;
            return;
          }

          this.initProfilePage(appProfile, creatorResult, creatorResult?.userId);
        },
        (error) => {
          if (this.helperService.retrieveErrorMessage(error) == "This user does not exist.") {
            this.appService.alert(this.translate.instant("com.profile_inactive_or_not_exist"));
            this.helperService.goBack();
          }
        }
      );
  }

  ngOnInit() {
    this.appService.applicationData$
      .pipe(
        filter((profile) => profile !== null),
        take(1)
      )
      .subscribe((profile) => {
        this.hasBeenInit = true;
        super.ngOnInit();

        const routeId = this.activatedRoute.snapshot.params["id"];
        this.routeProfileId = routeId || this.user["fc-id"] || this.user.id;

        const isUuid = (value: string): boolean => {
          if (!value || typeof value !== "string") return false;
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          return uuidRegex.test(value.trim());
        };

        if (!routeId) {
          this.account_id = this.user.id;
          this.initProfilePage(profile);
        } else if (isUuid(routeId)) {
          this.loadProfileWithPrivacyCheck(profile, this.routeProfileId);
        } else {
          this.loadProfileWithPrivacyCheck(profile, routeId);
        }
      });
  }

  private initProfilePage(appProfile: any, preloadedCreator?: any, firebaseUserId?: string) {
    if (this.displayedSection.id == "posts") {
      this.initPosts();
    } else if (this.displayedSection.id == "pinned_posts") {
      this.initPins();
    }

    this.communityService.getUserSkills(this.routeProfileId || this.account_id, true).subscribe((result) => {
      this.mySkills = result;
    });

    this.setupProfile(preloadedCreator, firebaseUserId);

    if (this.account_id == this.user.id) {
      this.communityService.getConnections(true).subscribe((result) => {
        this.setupConnections(result);
      });
    }

    this.checkConnectionStatus();
    this.workspaceService.getWorkspaces().subscribe((result) => {
      this.workspaces = result.workspaces;
    });
    this.exchangeService.categories$.subscribe((data) => (this.exchangeCategories = data));
    this.exchangeService.getCategories();
  }

  initPosts() {
    this.loadingPosts = true;
    this.communityService.unwatch();
    this.communityService.getPostsWithReplies({ postsAccountID: this.account_id, clear: true }).then((result: any) => {
      this.noPosts = !result || !result.length;
      this.loadingPosts = false;
      this.communityService.watchPostsWithReplies(false, false, this.account_id);
    });
  }

  moderate() {
    const url = `${environment.adminUrl}studiooneplus/moderate_posts?user_id=${this.account_id}`;
    window.open(url, "_blank");
  }

  openAddEditSkillsModal() {
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Profile" });
            const extra: Record<string, any> = {
              ctaText: this.mySkills && this.mySkills.length ? "Add More Skills" : "Add Skills",
              ctaLocation: "Profile About",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    const modalRef = this.appService.showModal(AddEditSkillComponent, { size: "lg" });
    modalRef.componentInstance.skillAdded.pipe(take(1)).subscribe(() => {
      this.communityService.getUserSkills(this.routeProfileId || this.account_id, true).subscribe((result) => {
        this.mySkills = result;
      });
    });
  }

  initPins() {
    this.loadingPins = true;
    this.communityService.unwatch();
    this.communityService.getPostsWithReplies({ limit: 500, clear: true, pins: true }).then((result: any) => {
      this.noPins = !result || !result.length;

      this.loadingPins = false;
      this.communityService.watchPostsWithReplies(false, false, false, true);
    });
  }

  setupProfile(preloadedCreator?: any, firebaseUserId?: string) {
    const idForFirebase = firebaseUserId ?? this.account_id;

    // Fetch Firebase profile only for supplementary fields (hasSetupCommunity, photoURL)
    this.communityService.getUserProfile(idForFirebase).then((fbProfile) => {
      if (fbProfile) {
        this.hasSetupCommunity = fbProfile.hasSetupCommunity;
      }
    });

    // Use preloadedCreator (from getProfileDetails / DB) as the source of truth
    if (preloadedCreator) {
      this.applyCreatorResult(preloadedCreator);
    } else {
      this.communityService.getProfileDetails(this.routeProfileId || this.account_id).subscribe(
        (creatorResult) => this.applyCreatorResult(creatorResult),
        (error) => {
          if (this.helperService.retrieveErrorMessage(error) == "This user does not exist.") {
            this.appService.alert(this.translate.instant("com.profile_inactive_or_not_exist"));
            this.helperService.goBack();
          }
        }
      );
    }

    this.communityService.getShowcases(this.routeProfileId || this.account_id).subscribe((result) => {
      this.showcases = result;
    });
  }

  private applyCreatorResult(creatorResult: any) {
    this.profile = creatorResult;
    if (creatorResult.isPublic != null) this.profile.isPublic = creatorResult.isPublic;
    if (!this.profile.photo && this.profile.photoURL) this.profile.photo = this.profile.photoURL;
    if (creatorResult.feature) {
      this.profile.feature = creatorResult.feature;
      this.isFeatured = true;
      if (creatorResult.feature.description) this.profile.description = creatorResult.feature.description;
      if (!this.profile.photo && creatorResult.feature.image_url) this.profile.photo = creatorResult.feature.image_url;
    }

    if (creatorResult.wares && Array.isArray(creatorResult.wares.data) && creatorResult.wares.data.length) {
      this.wares = creatorResult.wares;
      this.filteredWares = creatorResult.wares;
      const uniqueWareCategories = [
        ...new Set(
          creatorResult.wares.data.map(({ extension }) => {
            return extension == "instrument" || extension == "multisample" || extension == "soundx" ? "preset" : extension;
          })
        ),
      ];

      if (uniqueWareCategories.length > 1) {
        const defaultWareCategory = {
          title: "myitems",
          display_name: "All",
          total: creatorResult.wares.data.length,
        };
        this.displayedWareCategory = defaultWareCategory;
        this.wareCategories = [defaultWareCategory];

        creatorResult.wares.data.forEach(({ extension }) => {
          const catName = extension == "instrument" || extension == "multisample" || extension == "soundx" ? "preset" : extension;
          const catIndex = this.wareCategories.findIndex((i) => i.title == catName);

          if (catIndex >= 0) {
            this.wareCategories[catIndex].total += 1;
          } else if (this.exchangeCategories && this.exchangeCategories.length) {
            const match = this.exchangeCategories.find((i) => i.title == catName);
            this.wareCategories.push({
              title: catName,
              display_name: match?.display_name || extension,
              total: 1,
            });
          }
        });
      }
    } else {
      this.wares = undefined;
      this.filteredWares = undefined;
    }

    this.loading = false;
    if (this.profile.cover_photo && (this.profile.id == this.user.id || this.profile.isPublic || this.isFeatured)) {
      this.coverPhoto = encodeURI(this.profile.cover_photo);
    }
    if (this.profile.description) this.profile.htmlDescription = this.communityService.urlify(this.profile.description);
    if (this.profile.feature && this.profile.feature.description) this.profile.feature.htmlDescription = this.profile.feature.description;
  }

  setupConnections(result) {
    this.connections = result.filter((item) => item.status == "accepted" || item.initiated_by == this.user.id);
    this.invites = result.filter((item) => item.status == "pending" && item.initiated_by != this.user.id);
    this.connections.sort((a, b) => {
      return a.lastName > b.lastName ? 1 : -1;
    });
    this.invites.sort((a, b) => {
      return a.lastName > b.lastName ? 1 : -1;
    });

    this.displayedConnections = [...this.connections];
    // check query params
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      if (params && params.reviewConnectionFor) {
        let connection = this.invites.filter((item) => item.theirID == params.reviewConnectionFor)[0];
        if (connection) {
          this.reviewConnection(connection);
        } else {
          this.appService.alert("It looks like you've already responded to this connection request.");
        }
        this.router.navigate(["profile", this.routeProfileId || this.account_id, "connections"], {
          replaceUrl: true,
        });
      }
    });
  }

  doFilter() {
    if (this.connectionFilter) {
      this.displayedConnections = this.connections.filter(
        (item) => item.firstName.toLowerCase().indexOf(this.connectionFilter.toLowerCase()) > -1 || item.lastName.toLowerCase().indexOf(this.connectionFilter.toLowerCase()) > -1
      );
    } else {
      this.displayedConnections = [...this.connections];
    }
  }

  sendMessage() {
    this.router.navigate(["/messages", this.account_id]);
  }

  connectionOptions(event, connection) {
    event.stopPropagation();
    let args: any = {
      title: connection.firstName + " " + connection.lastName,
      actions: [],
    };
    args.actions.push("Remove Connection");

    if (connection.status == "pending") args.actions.push("Nudge " + connection.firstName);
    if (connection.status == "accepted") args.actions.push("Send a Message");
    args.actions.push("View " + connection.firstName + "'s Profile");
    if (this.workspaces && this.workspaces.length) args.actions.push("Add " + connection.firstName + " To a Workspace");
    args.actions.push("Create New Workspace With " + connection.firstName);

    this._eventbusService.emit(this._eventbusService.types.showActionChooser, args);
    this._eventbusService
      .observe(this._eventbusService.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          if (result.indexOf("Nudge") > -1) {
            this.sendConnectionInviteEmail(connection);
          } else if (result == "Remove Connection") {
            if (confirm("Are you sure you want to remove this connection?")) {
              this.removeConnection(connection.id, connection.theirID);
            }
          } else if (result.indexOf("Profile") > -1) {
            const profileId = this.userService.appId === "fender" && connection.their_fc_id ? connection.their_fc_id : connection.theirID;
            this.router.navigate(["../../profile/", profileId]);
          } else if (result.indexOf("Create") > -1) {
            let modal: any = AddWorkspaceComponent;
            const modalRef = this.appService.showModal(modal, { size: "lg" });
            modalRef.componentInstance.initialCollaborator = connection;
          } else if (result.indexOf("a Message") > -1) {
            const messageId = this.userService.appId === "fender" && connection.their_fc_id ? connection.their_fc_id : connection.theirID;
            this.router.navigate(["/messages", messageId]);
          } else {
            let items = [];
            this.workspaces.forEach((item) => {
              items.push({
                title: item.name,
                photo: item.image ? item.image : "./assets/icons/workspace.png",
                item: item,
              });
            });
            let modal: any = SelectListItemComponent;
            const modalRef = this.appService.showModal(modal, { size: "sm" });
            modalRef.componentInstance.items = items;
            modalRef.componentInstance.imageRound = false;
            modalRef.componentInstance.imageBorder = false;
            modalRef.componentInstance.title = "Add " + connection.firstName + " to a workspace...";
            modalRef.componentInstance.itemSelected.pipe(take(1)).subscribe((workspace) => {
              this.workspaceService.findCollaborator(connection.theirID, workspace.id).subscribe(
                (inviteResult) => {
                  let timelineRef = this.fbService.doc(`timelines/${workspace.firebase_timeline_id}`).ref;
                  this.fbService
                    .handleFirestorePromise(() => timelineRef.get())
                    .then((result) => {
                      let data: any = result.data();
                      let contributors = data.contributors;
                      contributors[connection.theirID] = true;
                      timelineRef.update({ contributors });
                    })
                    .catch((err) => {
                      // console.log('error', err)
                    });
                  this.loading = false;
                  this.appService.alert(connection.firstName + " was successfully added to " + workspace.name + ".");
                },
                (err) => {
                  this.appService.alert(err);
                }
              );
            });
          }
        }
      });
  }

  checkConnectionStatus() {
    this.connectionStatus = undefined;
    if (this.account_id != this.user.id) {
      let connectionStatus = this.communityService.connections.filter((item) => item.requested_user_id == this.account_id || item.initiated_by == this.account_id);
      if (connectionStatus && connectionStatus[0]) {
        this.connectionStatus = connectionStatus[0].status;
        this.connection = connectionStatus[0];
      }
    }
  }

  uploadWare() {
    const modalRef = this.appService.showModal(UploadWareComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.creator_id = this.account_id;
    this.exchangeService.wareDetail$.pipe(take(1)).subscribe((result) => {
      this.setupProfile();
    });
  }

  goToConnection(connection) {
    const profileId =
      this.userService.appId === "fender" && connection.their_fc_id ? connection.their_fc_id : connection.requested_user_id == this.user.id ? connection.initiated_by : connection.requested_user_id;
    this.router.navigate(["/profile", profileId]);
  }

  switchSection(id) {
    this.displayedSection = this.sections.filter((item) => item.id == id)[0];
    const routeProfileId = this.routeProfileId || this.account_id;
    let route = "profile/" + routeProfileId + "/" + id;
    this.location.replaceState(route);
    if (id == "posts") {
      this.initPosts();
    } else if (id == "pinned_posts") {
      this.initPins();
    }
  }

  makePublic() {
    if (this.user.id == this.profile.id) {
      this.userService.saveUser({ public: true }).subscribe((result) => {
        this.communityService.updateProfile({ public: true }).then((result) => {
          this.profile.isPublic = true;
        });
      });
    }
  }

  showConnectModal() {
    const modalRef = this.appService.showModal(AddConnectionComponent, { size: "md", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.title = `Connect with ${this.profile.firstName} ${this.profile.lastName}`;
    modalRef.componentInstance.requestConnection.subscribe((result) => {
      this.addConnection(result);
    });
  }

  /**
   * NO subscription gate. This used to run only for `user.active_subscription` and bounce
   * everyone else to `/onboarding?subscribeOnly=true` — Fender Studio Pro's membership rule
   * applied to an action that has nothing to do with any one community. A Tone Master Pro
   * member with no FSP subscription was sent to sign up for FSP for trying to connect with
   * someone. Same class of bug as the reaction gate in `comment.component`. paeapi's
   * `POST /community/connection` requires only authentication.
   */
  addConnection(message: string = "") {
    this.communityService.addConnection(this.account_id, message).subscribe((result) => {
      this.setupConnections(result);
      let connection = result.filter((item) => item.requested_user_id == this.account_id || item.initiated_by == this.account_id)[0];
      this.sendConnectionInviteEmail(connection);
      this.checkConnectionStatus();
      const te = this.avoService.trackEvent();
      if (this.userService.appId === "fender" && te.communityViewed) {
        const page = this.avoService.getPageProperties({ pageName: "Community Connection Sent" });
        const extra = {
          name: "Community Connection Sent",
          channelName: "profile",
          eventType: "connection",
          interactionType: "sent",
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.communityViewed(payload);
      } else if (te.communityConnectionSent) {
        te.communityConnectionSent({
          siteId: "my_fender",
        });
      }
    });
  }

  sendConnectionInviteEmail(connection) {
    let body = "";
    if (connection.message) body = connection.message;
    let notification = this.notificationService.createCommunityConnectionInvite(this.userService.user, connection, body);
    this.notificationService
      .sendNotification({
        notification: notification,
        user_ids: [connection.requested_user_id],
        clients: ["rollup", "mobile", "email"],
      })
      .subscribe((result) => {
        alert("Connection invite request notification has been sent!");
      });
  }

  removeConnection(id, user_id) {
    this.communityService.removeConnection(id, user_id).subscribe((result) => {
      this.setupConnections(result);
      this.checkConnectionStatus();
    });
  }

  updateConnection(status, connection, event?) {
    if (event) event.stopPropagation();
    connection.status = status;
    this.communityService
      .updateConnection({
        id: connection.id,
        status: connection.status,
      })
      .subscribe((result) => {
        this.setupConnections(result);
        this.checkConnectionStatus();
        if (status === "accepted") {
          const te = this.avoService.trackEvent();
          if (this.userService.appId === "fender" && te.communityViewed) {
            const page = this.avoService.getPageProperties({ pageName: "Community Connection Accepted" });
            const extra = {
              name: "Community Connection Accepted",
              channelName: "profile",
              eventType: "connection",
              interactionType: "accepted",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            te.communityViewed(payload);
          } else if (te.communityConnectionAccepted) {
            te.communityConnectionAccepted({
              siteId: "my_fender",
            });
          }
        }
      });
  }

  viewAsset(id) {
    this.router.navigate(["/exchange/detail/" + id]);
  }

  showOptions() {
    let args: any = {
      title: "Options",
      actions: ["Send a Message to " + this.profile.firstName, "Add " + this.profile.firstName + " To a Workspace", "Create New Workspace With " + this.profile.firstName],
    };
    this._eventbusService.emit(this._eventbusService.types.showActionChooser, args);
    this._eventbusService
      .observe(this._eventbusService.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          if (result.indexOf("Send a Message") > -1) {
            this.sendMessage();
          } else if (result.indexOf("Create") > -1) {
            let modal: any = AddWorkspaceComponent;
            const modalRef = this.appService.showModal(modal, { size: "lg" });
            modalRef.componentInstance.initialCollaborator = this.connection;
          } else {
            this.workspaceService.getWorkspaces().subscribe((result) => {
              let items = [];
              result.workspaces.forEach((item) => {
                items.push({
                  title: item.name,
                  photo: item.image ? item.image : "./assets/icons/workspace.png",
                  item: item,
                });
              });
              let modal: any = SelectListItemComponent;
              const modalRef = this.appService.showModal(modal, { size: "sm" });
              modalRef.componentInstance.items = items;
              modalRef.componentInstance.imageRound = false;
              modalRef.componentInstance.imageBorder = false;
              modalRef.componentInstance.title = "Add " + this.connection.firstName + " to a workspace...";
              modalRef.componentInstance.itemSelected.pipe(take(1)).subscribe((workspace) => {
                this.workspaceService.findCollaborator(this.connection.theirID, workspace.id).subscribe(
                  (result) => {
                    this.loading = false;
                    this.appService.alert(this.connection.firstName + " was successfully added to " + workspace.name + ".");
                  },
                  (err) => {
                    this.appService.alert(err);
                  }
                );
              });
            });
          }
        }
      });
  }

  selectWareCategory(categoryTitle) {
    const category = this.wareCategories.find(({ title }) => title === categoryTitle);
    this.displayedWareCategory = category;

    if (category.title === "myitems") {
      this.filteredWares = this.wares;
    } else {
      const filterArray = category.title == "preset" ? ["preset", "instrument", "multisample", "soundx"] : [category.title];
      const filteredWares = this.wares.data.filter((ware) => filterArray.indexOf(ware.extension) >= 0);
      this.filteredWares = { data: filteredWares, total: category.total };
    }
  }

  reviewConnection(connection) {
    const modal = this.appService.showModal(ReviewConnectionComponent, { size: "md" });
    modal.componentInstance.connection = connection;
    modal.componentInstance.connectionRequestResponse.subscribe((response) => {
      this.updateConnection(response, connection);
    });
    modal.componentInstance.viewRequesterProfile.subscribe((_) => {
      this.goToConnection(connection);
    });
  }

  ngOnDestroy() {
    this.hasBeenInit = false;
    this.communityService.unwatch();
    this.appService.fullscreenHeader = undefined;
  }
}
